import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEventBus } from '@vueuse/core'
import axios from 'axios'
import { EventKey } from '@/core/events/event-key'
import useDelete from '@/core/composables/api/useDelete'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import type { TeamListItem } from '../models/organization-team'

export interface CreateTeamBody {
  name: string
  memberIds?: string[]
}

export type TeamMutationResult = true | false | 'invalid-name'

const statusCode = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status
  }
  return undefined
}

export const useOrganizationTeams = () => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const teamCreatedBus = useEventBus(EventKey.teamCreated)
  const teamUpdatedBus = useEventBus(EventKey.teamUpdated)
  const { data, status, load, reload } = useGet<TeamListItem[]>('admin/teams')

  const {
    mutate: createTeamRequest,
    status: createStatus,
    error: createError,
    data: createdTeamId,
  } = usePost<string, CreateTeamBody>('admin/teams')

  const deleteEndpoint = ref('admin/teams')
  const {
    remove: deleteTeamRequest,
    status: deleteStatus,
    error: deleteError,
  } = useDelete<void>(deleteEndpoint)

  onMounted(() => {
    load()
  })

  const createTeam = async (body: CreateTeamBody): Promise<string | null | 'invalid-name'> => {
    await createTeamRequest({
      name: body.name.trim(),
      ...(body.memberIds?.length ? { memberIds: body.memberIds } : {}),
    })

    if (createStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('settings.organizationTeams.snackbar.createSuccess'))
      teamCreatedBus.emit({ teamId: createdTeamId.value ?? '' })
      await reload()
      return createdTeamId.value
    }

    if (statusCode(createError.value) === 400) {
      return 'invalid-name'
    }
    if (statusCode(createError.value) === 404) {
      snackStore.showError(t('settings.organizationTeams.snackbar.memberNotFound'))
    } else {
      snackStore.showError(t('settings.organizationTeams.snackbar.createError'))
    }
    return null
  }

  const deleteTeam = async (teamId: string): Promise<boolean> => {
    deleteEndpoint.value = `admin/teams/${teamId}`
    await deleteTeamRequest()

    if (deleteStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('settings.organizationTeams.snackbar.deleteSuccess'))
      teamUpdatedBus.emit({ teamId })
      await reload()
      return true
    }

    snackStore.showError(
      statusCode(deleteError.value) === 404
        ? t('settings.organizationTeams.snackbar.notFound')
        : t('settings.organizationTeams.snackbar.deleteError'),
    )
    return false
  }

  return {
    teams: data,
    status,
    createTeam,
    deleteTeam,
  }
}
